import React from "react";
import { Row, Col } from "reactstrap";
import Select from "react-select/creatable";
import { components } from "react-select";
import { useAcquirerForm } from "../hooks/useAcquirerForm";
import { CARD_FLAGS } from "../constants";
import { CardHelper } from "../../../../utils/helpers";
import { OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import PropTypes from "prop-types";

/**
 * CustomOption
 * Componente visual para cada opção do react-select.
 */
const CustomOption = (props) => {
    const { label, data, isSelected } = props;
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center gap-2">
                <i className={`${data?.icon || 'mdi mdi-plus-circle-outline'} ${isSelected ? 'text-white' : 'text-muted'}`}></i>
                <span>{label}</span>
            </div>
        </components.Option>
    );
};

/**
 * CustomMenuList
 * Adiciona um rodapé no menu do Select com dica para criar nova bandeira.
 */
const CustomMenuList = (props) => {
    const { children, innerRef, innerProps } = props;
    return (
        <div ref={innerRef} {...innerProps}>
            {children}
            <div className="px-3 py-2 border-top bg-light d-flex align-items-center gap-2 text-muted" style={{ fontSize: '11px', borderTop: "1.5px solid rgba(72,39,18,0.1)" }}>
                <i className="mdi mdi-lightbulb-on-outline text-warning"></i>
                <span style={{ fontFamily: "Outfit, sans-serif", fontWeight: 500 }}>Digite o nome de uma bandeira personalizada e pressione Enter para criá-la.</span>
            </div>
        </div>
    );
};

/**
 * CustomDropdownIndicator & CustomClearIndicator
 * Componentes de seta e limpeza estilizados para combinar com os campos padrão do formulário.
 */
const CustomDropdownIndicator = (props) => {
    return (
        <div {...props.innerProps} style={{ padding: '0 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <i className="mdi mdi-chevron-down" style={{ color: '#482712', fontSize: '18px' }}></i>
        </div>
    );
};

const CustomClearIndicator = (props) => {
    return (
        <div {...props.innerProps} style={{ padding: '0 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <i className="mdi mdi-close" style={{ color: '#784b22', fontSize: '16px' }}></i>
        </div>
    );
};

/**
 * AcquirerEditor
 * Componente PURAMENTE VISUAL para edição de Adquirentes.
 * Migrado para o Neo Design System.
 */
const AcquirerEditor = ({ acquirer, bankAccounts, onSave, onDelete, onCancel, isSaving, onChange }) => {
    const {
        formik,
        selectedFlagId,
        setSelectedFlagId,
        flagOptions,
        selectedOptions,
        actions
    } = useAcquirerForm(acquirer, onSave, onCancel, onChange);

    const { values, errors, touched, handleChange, handleSubmit } = formik;

    if (!acquirer) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5" style={{ minHeight: "500px" }}>
                <div className="text-center py-5">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-credit-card-plus"></i>
                        </div>
                    </div>
                    <h5 className="fw-bold text-dark font-size-16" style={{ fontFamily: "Outfit, sans-serif" }}>Selecione uma adquirente</h5>
                    <p className="text-muted font-size-13 mx-auto" style={{ maxWidth: '320px' }}>
                        Escolha uma adquirente ao lado ou cadastre uma nova para configurar as taxas e parcelas.
                    </p>
                </div>
            </div>
        );
    }

    const currentFlagRates = values.rates?.[selectedFlagId];
    const rateKeys = Object.keys(values.rates || {});

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{values.id ? values.name : "Nova Adquirente"}</span>
                    {values.id && (
                        <NeoBadge color={values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {values.status === GLOBAL_STATUS.ACTIVE ? 'Ativa' : 'Inativa'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Taxas e Parcelamentos"
            headerIcon="mdi-calculator-variant"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {values.id && (
                        <NeoButton
                            type="button"
                            variant="outline-danger"
                            square={true}
                            onClick={() => onDelete(values.id, values.name)}
                            disabled={isSaving}
                            title="Excluir Adquirente"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    {formik.dirty && (
                        <NeoButton
                            type="button"
                            variant="outline-primary"
                            onClick={actions.handleCancel}
                            disabled={isSaving}
                        >
                            Cancelar
                        </NeoButton>
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn h-100 d-flex flex-column"
            bodyClassName="p-4 overflow-auto"
            noBorder={true}
        >
            <style>{`
                .react-select-container input,
                .react-select-container input[type="text"] {
                    background-color: transparent !important;
                    border: none !important;
                    border-radius: 0 !important;
                    padding: 0 !important;
                    box-shadow: none !important;
                    min-height: auto !important;
                    height: auto !important;
                    color: #482712 !important;
                }
            `}</style>
            
            <OverlayLoader show={isSaving} label="Salvando..." />

            {/* ─── DADOS PRINCIPAIS ─── */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-4 d-flex align-items-center" style={{ letterSpacing: "0.5px" }}>
                <i className="mdi mdi-information-outline me-2 font-size-15 text-primary"></i>
                Dados Principais
            </h6>
            <Row className="mb-4">
                <Col md={8} className="mb-3">
                    <NeoInput
                        label="Nome da Adquirente / Maquininha *"
                        name="name"
                        flat={true}
                        placeholder="Ex: Stone, Cielo..."
                        error={touched.name && errors.name ? errors.name : ""}
                        {...formik.getFieldProps("name")}
                    />
                </Col>
                <Col md={4} className="mb-3">
                    <NeoInput
                        label="Status"
                        type="select"
                        name="status"
                        flat={true}
                        {...formik.getFieldProps("status")}
                    >
                        <option value={GLOBAL_STATUS.ACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.ACTIVE]}</option>
                        <option value={GLOBAL_STATUS.INACTIVE}>{GLOBAL_STATUS_LABELS[GLOBAL_STATUS.INACTIVE]}</option>
                    </NeoInput>
                </Col>
            </Row>

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            {/* ─── SELEÇÃO DE BANDEIRAS (MultiSelect) ─── */}
            <div className="mb-4">
                <label className="neo-input-label d-flex align-items-center mb-2">
                    <i className="mdi mdi-credit-card-plus-outline me-2 font-size-15 text-primary"></i>
                    Bandeiras Habilitadas
                </label>
                <Select
                    isMulti
                    options={flagOptions}
                    value={selectedOptions}
                    onChange={actions.handleSelectChange}
                    placeholder="Busque ou digite o nome de uma bandeira..."
                    formatCreateLabel={(inputValue) => `➕ Criar bandeira: "${inputValue}"`}
                    noOptionsMessage={() => "Digite o nome da nova bandeira e pressione Enter"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    components={{
                        Option: CustomOption,
                        MenuList: CustomMenuList,
                        DropdownIndicator: CustomDropdownIndicator,
                        ClearIndicator: CustomClearIndicator,
                        IndicatorSeparator: null
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                        control: (base, state) => ({
                            ...base,
                            backgroundColor: '#f8f9fa',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '2px 8px',
                            minHeight: '38px',
                            boxShadow: state.isFocused ? '0 0 0 2px rgba(226, 102, 28, 0.2)' : 'none',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#f1f3f5'
                            }
                        }),
                        menu: (base) => ({
                            ...base,
                            borderRadius: '10px',
                            border: '1px solid rgba(72, 39, 18, 0.12)',
                            boxShadow: '0 10px 30px rgba(72, 39, 18, 0.08)',
                            overflow: 'hidden',
                            marginTop: '4px',
                            zIndex: 9999
                        }),
                        option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected 
                                ? '#482712' 
                                : state.isFocused 
                                    ? 'rgba(226, 102, 28, 0.06)' 
                                    : 'white',
                            color: state.isSelected ? 'white' : '#482712',
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 600,
                            fontSize: '13px',
                            cursor: 'pointer',
                            '&:active': {
                                backgroundColor: '#e2661c'
                            }
                        }),
                        multiValue: (base) => ({
                            ...base,
                            backgroundColor: 'rgba(226, 102, 28, 0.08)',
                            borderRadius: '6px',
                            border: '1px solid rgba(226, 102, 28, 0.15)'
                        }),
                        multiValueLabel: (base) => ({
                            ...base,
                            color: '#482712',
                            fontFamily: 'Outfit, sans-serif',
                            fontWeight: 700,
                            fontSize: '11px',
                            padding: '2px 6px'
                        }),
                        multiValueRemove: (base) => ({
                            ...base,
                            color: '#e2661c',
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#e2661c',
                                color: 'white',
                                borderRadius: '0 6px 6px 0'
                            }
                        }),
                        placeholder: (base) => ({
                            ...base,
                            color: '#a38a70',
                            fontFamily: 'Outfit, sans-serif',
                            fontSize: '13px',
                            fontWeight: 500
                        }),
                        input: (base) => ({
                            ...base,
                            color: '#482712',
                            fontFamily: 'Outfit, sans-serif',
                            fontSize: '13px',
                            fontWeight: 500
                        }),
                        menuPortal: base => ({ ...base, zIndex: 9999 })
                    }}
                />
                <small className="text-muted d-block mt-2 italic">* Selecione as bandeiras desejadas ou digite o nome de uma nova para configurar.</small>
            </div>

            {/* ─── ABAS DE BANDEIRAS + EDITOR DE TAXAS ─── */}
            {rateKeys.length > 0 && (
                <>
                    <div className="mb-4">
                        <label className="neo-input-label mb-2">Editar Taxas de:</label>
                        <div className="d-flex flex-wrap gap-2">
                            {rateKeys.map(fId => (
                                <NeoButton
                                    key={fId}
                                    variant={selectedFlagId === fId ? "primary" : "outline-primary"}
                                    className="d-flex align-items-center gap-2"
                                    onClick={() => setSelectedFlagId(fId)}
                                >
                                    <i className={CardHelper.getFlagIcon(fId, CARD_FLAGS)}></i>
                                    {CardHelper.getFlagName(fId, CARD_FLAGS)}
                                </NeoButton>
                            ))}
                        </div>
                    </div>

                    {selectedFlagId && currentFlagRates && (
                        <div 
                            className="p-4 bg-white shadow-sm mb-4"
                            style={{
                                borderRadius: "12px",
                                border: "1.5px solid rgba(72,39,18,0.12)"
                            }}
                        >
                            {/* Header da bandeira selecionada */}
                            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-2"
                                        style={{
                                            width: "28px",
                                            height: "28px",
                                            background: "rgba(226, 102, 28, 0.08)",
                                            border: "1px solid rgba(226, 102, 28, 0.15)"
                                        }}
                                    >
                                        <i className={`${CardHelper.getFlagIcon(selectedFlagId, CARD_FLAGS)} font-size-14`} style={{ color: "#e2661c" }}></i>
                                    </div>
                                    <h6 className="mb-0 fw-bold" style={{ color: "#482712" }}>{CardHelper.getFlagName(selectedFlagId, CARD_FLAGS)}</h6>
                                </div>
                                <div className="d-flex gap-2">
                                    <NeoButton
                                        variant="outline-danger"
                                        type="button"
                                        className="btn-sm px-3"
                                        onClick={() => actions.handleRemoveFlag(selectedFlagId)}
                                    >
                                        <i className="mdi mdi-trash-can-outline me-1"></i> Remover
                                    </NeoButton>
                                    <NeoButton
                                        variant="outline-primary"
                                        type="button"
                                        className="btn-sm px-3"
                                        onClick={actions.handleLoadDefaults}
                                    >
                                        <i className="mdi mdi-refresh me-1"></i> Carregar Padrão
                                    </NeoButton>
                                </div>
                            </div>

                            {/* Conteúdo da bandeira: PIX vs Cartão */}
                            {selectedFlagId === 'pix' ? (
                                <Row>
                                    <Col md={4}>
                                        <NeoInput
                                            label="Taxa PIX (%)"
                                            type="number"
                                            step="0.01"
                                            name={`rates.${selectedFlagId}.rate`}
                                            flat={true}
                                            className="fw-bold text-primary text-end"
                                            value={currentFlagRates.rate || 0}
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </Row>
                            ) : (
                                <>
                                    {/* Taxa Débito */}
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <NeoInput
                                                label="Taxa Débito (%)"
                                                type="number"
                                                step="0.01"
                                                name={`rates.${selectedFlagId}.debit`}
                                                flat={true}
                                                className="fw-bold text-success text-end"
                                                value={currentFlagRates.debit || 0}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                    </Row>

                                    {/* Tabela de Crédito / Parcelamentos */}
                                    <div className="mb-3 d-flex justify-content-between align-items-center">
                                        <label className="neo-input-label mb-0">Crédito e Parcelamentos</label>
                                        <NeoButton
                                            variant="outline-primary"
                                            className="btn-sm px-3"
                                            type="button"
                                            onClick={actions.handleAddInstallment}
                                        >
                                            <i className="mdi mdi-plus-circle-outline me-1"></i> Nova Taxa
                                        </NeoButton>
                                    </div>

                                    <div className="table-responsive border mb-0" style={{ borderRadius: "8px" }}>
                                        <table className="table table-hover table-sm mb-0 align-middle">
                                            <thead>
                                                <tr className="bg-light text-muted text-uppercase font-size-11 fw-bold" style={{ fontFamily: "Outfit, sans-serif", borderBottom: "1.5px solid rgba(72,39,18,0.12)" }}>
                                                    <th width="40%" className="ps-3 py-2 small text-uppercase text-muted">Parcelas</th>
                                                    <th className="text-end py-2 small text-uppercase text-muted">Taxa (%)</th>
                                                    <th width="60" className="text-center py-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(currentFlagRates.credit || []).map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="ps-3">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <input
                                                                    type="number"
                                                                    name={`rates.${selectedFlagId}.credit.${index}.installments`}
                                                                    value={item.installments}
                                                                    onChange={handleChange}
                                                                    className="form-control form-control-sm bg-light border-0 fw-bold text-center"
                                                                    style={{ width: '60px', borderRadius: "6px" }}
                                                                />
                                                                <span className="small text-muted fw-bold">x</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                name={`rates.${selectedFlagId}.credit.${index}.rate`}
                                                                value={item.rate}
                                                                onChange={handleChange}
                                                                className="form-control form-control-sm text-end bg-light border-0 fw-bold text-dark"
                                                                placeholder="0.00"
                                                                style={{ borderRadius: "6px" }}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <NeoButton
                                                                variant="outline-danger"
                                                                square={true}
                                                                className="btn-sm p-1 border-0"
                                                                onClick={() => actions.handleRemoveInstallment(index)}
                                                            >
                                                                <i className="mdi mdi-trash-can-outline"></i>
                                                            </NeoButton>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(!currentFlagRates.credit || currentFlagRates.credit.length === 0) && (
                                                    <tr>
                                                        <td colSpan="3" className="text-center py-4 text-muted small italic">
                                                            Nenhuma taxa de crédito configurada.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Estado vazio de bandeiras */}
            {rateKeys.length === 0 && (
                <div className="text-center py-5 border bg-white shadow-sm mb-4" style={{ borderRadius: "12px", border: "2px dashed rgba(72,39,18,0.15)", borderStyle: "dashed" }}>
                    <i className="mdi mdi-credit-card-outline font-size-40 text-muted mb-2 d-block"></i>
                    <p className="text-muted mb-0 font-size-13 fw-semibold">Habilite ao menos uma bandeira acima para configurar as taxas.</p>
                </div>
            )}

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            {/* ─── ANTECIPAÇÃO DE RECEBÍVEIS ─── */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 d-flex align-items-center" style={{ letterSpacing: "0.5px" }}>
                <i className="mdi mdi-clock-fast me-2 font-size-15 text-warning"></i>
                Antecipação de Recebíveis
            </h6>

            <Row className="mb-4">
                <Col md={3} className="mb-3">
                    <div className="neo-input-wrapper neo-input-wrapper-md">
                        <label className="neo-input-label">Antecipação</label>
                        <div className="form-check form-switch pt-2">
                            <input
                                type="checkbox"
                                role="switch"
                                checked={!!values.anticipation?.enabled}
                                onChange={(e) => {
                                    formik.setFieldValue("anticipation.enabled", e.target.checked);
                                }}
                                className="form-check-input cursor-pointer"
                                id="switch-anticipation"
                            />
                            <label className="ms-2 small text-muted cursor-pointer" htmlFor="switch-anticipation">
                                {values.anticipation?.enabled ? 'Ativada' : 'Desativada'}
                            </label>
                        </div>
                    </div>
                </Col>

                {values.anticipation?.enabled && (
                    <>
                        <Col md={3} className="mb-3">
                            <NeoInput
                                label="Tipo"
                                name="anticipation.type"
                                type="select"
                                flat={true}
                                value={values.anticipation?.type || "automatic"}
                                onChange={handleChange}
                            >
                                <option value="automatic">Automática (tudo antecipa)</option>
                                <option value="manual">Manual (por venda)</option>
                            </NeoInput>
                        </Col>
                        <Col md={3} className="mb-3">
                            <NeoInput
                                label="Taxa / Mês (%)"
                                type="number"
                                step="0.01"
                                name="anticipation.feePerMonth"
                                flat={true}
                                className="fw-bold text-warning text-end"
                                value={values.anticipation?.feePerMonth || 0}
                                onChange={handleChange}
                            />
                            <small className="text-muted font-size-11">Taxa por mês antecipado</small>
                        </Col>
                        <Col md={3} className="mb-3">
                            <NeoInput
                                label="Prazo de Depósito (D+X)"
                                type="number"
                                min="0"
                                max="31"
                                name="anticipation.settlementDays"
                                flat={true}
                                className="fw-bold text-primary text-end"
                                value={values.anticipation?.settlementDays || 1}
                                onChange={handleChange}
                            />
                            <small className="text-muted font-size-11">Dias para o dinheiro cair na conta</small>
                        </Col>
                    </>
                )}
            </Row>

            <hr className="my-4 border-dashed" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }} />

            {/* ─── CONTA BANCÁRIA DE DEPÓSITO ─── */}
            <h6 className="text-uppercase font-size-11 fw-bold text-muted mb-3 d-flex align-items-center" style={{ letterSpacing: "0.5px" }}>
                <i className="mdi mdi-bank-outline me-2 font-size-15 text-info"></i>
                Conta Bancária de Depósito
            </h6>
            <Row>
                <Col md={6} className="mb-3">
                    <NeoInput
                        label="Conta de Destino"
                        type="select"
                        name="idBankAccount"
                        flat={true}
                        value={values.idBankAccount || ""}
                        onChange={(e) => {
                            const selectedId = e.target.value;
                            const selectedAccount = (bankAccounts || []).find(a => a.id === selectedId);
                            formik.setFieldValue("idBankAccount", selectedId);
                            formik.setFieldValue("bankAccountName", selectedAccount ? selectedAccount.description : "");
                        }}
                    >
                        <option value="">Selecione uma conta bancária...</option>
                        {(bankAccounts || []).map(account => (
                            <option key={account.id} value={account.id}>{account.description}</option>
                        ))}
                    </NeoInput>
                    <small className="text-muted font-size-11">A adquirente deposita os valores nesta conta</small>
                </Col>
            </Row>
        </NeoCard>
    );
};

AcquirerEditor.propTypes = {
    acquirer: PropTypes.object,
    bankAccounts: PropTypes.array,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default AcquirerEditor;
